import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import GiftCardIcon from "@/components/GiftCardIcon";
import { ArrowRight, CreditCard, Camera, Wallet, CheckCircle2, Clock, Users, Zap } from "lucide-react";

// ── Scroll reveal ────────────────────────────────────────────────────────────
const reduceMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// true once the element has scrolled into view (stays true)
export function useInView(opts = { threshold: 0.18 }) {
    const ref = useRef(null);
    const [seen, setSeen] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (typeof IntersectionObserver === "undefined" || reduceMotion()) { setSeen(true); return; }
        const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, opts);
        io.observe(el);
        return () => io.disconnect();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return [ref, seen];
}

// Fades + slides children up when they scroll into view. delay in ms.
export function Reveal({ children, delay = 0, y = 28, className = "", as: Tag = "div", style, ...rest }) {
    const [ref, seen] = useInView();
    return (
        <Tag ref={ref} className={className} {...rest}
            style={{
                ...style,                       // keep the section's own background etc.
                opacity: seen ? 1 : 0, transform: seen ? "none" : `translateY(${y}px)`,
                transition: `opacity .7s cubic-bezier(.2,.7,.2,1) ${delay}ms, transform .7s cubic-bezier(.2,.7,.2,1) ${delay}ms`,
                willChange: "opacity, transform",
            }}>
            {children}
        </Tag>
    );
}

// Counts from 0 to `to` once visible
function CountUp({ to, suffix = "", duration = 1400 }) {
    const [ref, seen] = useInView();
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!seen || !to) return;
        if (reduceMotion()) { setN(to); return; }
        let raf; const t0 = performance.now();
        const tick = (t) => {
            const p = Math.min(1, (t - t0) / duration);
            setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [seen, to, duration]);
    return <span ref={ref}>{n.toLocaleString()}{suffix}</span>;
}

// ── Features ─────────────────────────────────────────────────────────────────
const GRADS = [
    "from-indigo-500 to-violet-500", "from-amber-400 to-orange-500", "from-emerald-400 to-teal-500",
    "from-orange-400 to-rose-500", "from-violet-500 to-fuchsia-500",
];

export function FeaturesGrid({ features }) {
    return (
        <div className="bg-white rounded-3xl shadow-card border border-slate-100 grid grid-cols-2 md:grid-cols-5 gap-2 p-4 sm:p-6 md:p-8">
            {features.map((f, i) => (
                <Reveal key={f.title} delay={i * 90} className={i === features.length - 1 ? "col-span-2 md:col-span-1" : ""}>
                    <div className="group h-full p-4 rounded-2xl transition-all duration-300 hover:-translate-y-1 hover:bg-slate-50 hover:shadow-card">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 text-white bg-gradient-to-br ${GRADS[i % GRADS.length]} shadow-lg shadow-slate-200 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                            <f.icon size={20} />
                        </div>
                        <div className="font-heading font-semibold text-slate-900">{f.title}</div>
                        <div className="text-sm text-slate-500 mt-1">{f.desc}</div>
                    </div>
                </Reveal>
            ))}
        </div>
    );
}

// ── How it works ─────────────────────────────────────────────────────────────
function StepArt({ i, cards, on }) {
    if (i === 0) {
        const pick = (cards.length ? cards : [{ code: "AMAZON" }, { code: "ITUNES" }, { code: "STEAM" }, { code: "GOOGLEPLAY" }]).slice(0, 4);
        return (
            <div className="grid grid-cols-4 gap-2">
                {pick.map((c, k) => (
                    <div key={c.code} className={`rounded-xl bg-white border p-1.5 flex items-center justify-center transition-all duration-500 ${k === 0 && on ? "border-emerald-400 ring-2 ring-emerald-200 scale-105" : "border-slate-100"}`}
                        style={{ transitionDelay: `${300 + k * 80}ms` }}>
                        <GiftCardIcon code={c.code} iconUrl={c.icon_url} size={32} />
                    </div>
                ))}
            </div>
        );
    }
    if (i === 1) {
        return (
            <div className="relative h-[52px] rounded-xl border-2 border-dashed border-sky-200 bg-sky-50/50 flex items-center gap-2 px-3 overflow-hidden">
                <Camera size={18} className="text-sky-500 shrink-0" />
                <div className="flex-1 h-2 rounded-full bg-sky-100 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-sky-400 to-emerald-400 transition-all ease-out" style={{ width: on ? "100%" : "0%", transitionDuration: "1.4s", transitionDelay: "400ms" }} />
                </div>
                <CheckCircle2 size={18} className="text-emerald-500 shrink-0 transition-all duration-300" style={{ opacity: on ? 1 : 0, transform: on ? "scale(1)" : "scale(.4)", transitionDelay: "1.7s" }} />
            </div>
        );
    }
    return (
        <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white px-3 py-2 flex items-center justify-between">
            <div>
                <div className="text-[10px] opacity-80">GiftToCash Wallet</div>
                <div className="font-heading font-bold text-lg leading-tight transition-all duration-500" style={{ opacity: on ? 1 : 0, transform: on ? "none" : "translateY(6px)", transitionDelay: "900ms" }}>+ $48.00</div>
            </div>
            <Wallet size={22} className="opacity-90" />
        </div>
    );
}

const STEP_ICONS = [CreditCard, Camera, Wallet];
const STEP_TONES = ["from-indigo-500 to-violet-500", "from-sky-500 to-cyan-500", "from-emerald-500 to-teal-500"];

export function HowItWorks({ steps, cards = [] }) {
    const [ref, on] = useInView({ threshold: 0.25 });
    return (
        <div ref={ref} className="relative">
            {/* connecting line (desktop) */}
            <div aria-hidden className="hidden md:block absolute top-[46px] left-[16%] right-[16%] h-1 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-indigo-400 via-sky-400 to-emerald-400 transition-all ease-out" style={{ width: on ? "100%" : "0%", transitionDuration: "1.6s" }} />
            </div>
            <div className="grid md:grid-cols-3 gap-6 relative">
                {steps.map((s, i) => {
                    const Icon = STEP_ICONS[i] || CreditCard;
                    return (
                        <div key={s.n} className="relative" style={{ opacity: on ? 1 : 0, transform: on ? "none" : "translateY(30px)", transition: `all .7s cubic-bezier(.2,.7,.2,1) ${i * 220}ms` }}>
                            <div className="flex justify-center">
                                <div className={`relative w-[92px] h-[92px] rounded-3xl bg-gradient-to-br ${STEP_TONES[i]} text-white flex items-center justify-center shadow-xl shadow-slate-200 ring-8 ring-white`}>
                                    <Icon size={34} />
                                    <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center ring-4 ring-white">{s.n}</span>
                                </div>
                            </div>
                            <div className="mt-5 bg-cream rounded-2xl p-6 border border-slate-100 hover:shadow-card transition-shadow text-center md:text-left">
                                <div className="font-heading font-semibold text-xl text-slate-900">{s.title}</div>
                                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{s.desc}</p>
                                <div className="mt-4"><StepArt i={i} cards={cards} on={on} /></div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── Stats + payout strip ─────────────────────────────────────────────────────
const PAY_COLORS = {
    paypal: "bg-[#003087]", cashapp: "bg-[#00C244]", interac: "bg-[#F5A800]", "e-transfer": "bg-[#F5A800]", bank: "bg-slate-700",
    usdt: "bg-[#26A17B]", btc: "bg-[#F7931A]", bitcoin: "bg-[#F7931A]", eth: "bg-[#627EEA]", ethereum: "bg-[#627EEA]", usdc: "bg-[#2775CA]",
};
const payColor = (m) => {
    const k = `${m.code} ${m.name}`.toLowerCase();
    return Object.entries(PAY_COLORS).find(([key]) => k.includes(key))?.[1] || "bg-slate-600";
};

export function StatsStrip({ cardCount }) {
    const [methods, setMethods] = useState([]);
    useEffect(() => {
        const t = setTimeout(() => {
            api.get("/payout-methods").then((r) => setMethods((r.data || []).filter((m) => m.code !== "WALLET" && m.type !== "wallet"))).catch(() => {});
        }, 700);
        return () => clearTimeout(t);
    }, []);
    const stats = [
        { icon: Users, value: 10000, suffix: "+", label: "Happy users" },
        { icon: CreditCard, value: cardCount, suffix: "", label: "Gift card brands" },
        { icon: Wallet, value: methods.length, suffix: "", label: "Ways to get paid" },
        { icon: Clock, text: "24/7", label: "Support" },
    ];
    return (
        <div className="relative rounded-3xl overflow-hidden text-white" style={{ background: "linear-gradient(135deg, #064e3b 0%, #0f766e 45%, #0f172a 100%)" }}>
            <div aria-hidden className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl" />
            <div aria-hidden className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-teal-300/10 blur-3xl" />
            <div className="relative px-6 sm:px-10 py-10">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {stats.map((s, i) => (
                        <Reveal key={s.label} delay={i * 110} className="text-center lg:text-left">
                            <div className="inline-flex w-10 h-10 rounded-xl bg-white/10 items-center justify-center mb-2"><s.icon size={18} /></div>
                            <div className="font-heading font-black text-3xl sm:text-4xl tracking-tight">
                                {s.text || (s.value ? <CountUp to={s.value} suffix={s.suffix} /> : "—")}
                            </div>
                            <div className="text-emerald-100/80 text-sm">{s.label}</div>
                        </Reveal>
                    ))}
                </div>
                {methods.length > 0 && (
                    <Reveal delay={300} className="mt-8 pt-6 border-t border-white/10">
                        <div className="text-xs uppercase tracking-[0.2em] text-emerald-100/70 mb-3 text-center lg:text-left">Withdraw your way</div>
                        <div className="flex flex-wrap justify-center lg:justify-start gap-2">
                            {methods.map((m, i) => (
                                <span key={m.code} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur rounded-full pl-1 pr-3 py-1 text-sm transition-colors"
                                    style={{ animation: `fadeUp .5s ease ${400 + i * 70}ms both` }}>
                                    {m.icon_url
                                        ? <img src={m.icon_url.startsWith("http") ? m.icon_url : `${process.env.REACT_APP_BACKEND_URL || ""}${m.icon_url}`} alt="" className="w-6 h-6 rounded-full object-cover bg-white" loading="lazy" />
                                        : <span className={`w-6 h-6 rounded-full ${payColor(m)} text-[10px] font-bold flex items-center justify-center`}>{(m.name || m.code)[0]}</span>}
                                    {m.name}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                )}
            </div>
            <style>{"@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}"}</style>
        </div>
    );
}

// ── Live rates chart ─────────────────────────────────────────────────────────
const BAR = ["from-emerald-400 to-teal-500", "from-sky-400 to-indigo-500", "from-amber-400 to-orange-500", "from-violet-400 to-fuchsia-500", "from-rose-400 to-pink-500"];

export function RatesChart({ cards }) {
    const [cur, setCur] = useState("USD");
    const [ref, on] = useInView({ threshold: 0.2 });
    const sym = cur === "CAD" ? "CA$" : "$";
    const rows = [...cards]
        .map((c) => ({ ...c, r: Number(cur === "CAD" ? (c.rate_cad ?? c.rate) : c.rate) || 0 }))
        .filter((c) => c.r > 0)                 // no offer in this currency -> not listed
        .sort((a, b) => b.r - a.r).slice(0, 7);
    const top = Math.max(...rows.map((x) => x.r), 0.01);
    return (
        <div ref={ref} className="bg-white rounded-3xl border border-slate-100 shadow-card overflow-hidden" data-testid="live-rates-table">
            <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-3 bg-slate-50 border-b border-slate-100">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    <span className="relative flex w-2 h-2"><span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" /><span className="relative w-2 h-2 rounded-full bg-emerald-500" /></span>
                    Live rates · for every {sym}100
                </div>
                <div className="inline-flex bg-white rounded-full p-1 border border-slate-200">
                    {["USD", "CAD"].map((c) => (
                        <button key={c} onClick={() => { setCur(c); try { window.dispatchEvent(new CustomEvent("g2c:rates-currency", { detail: { currency: c } })); } catch (_) {} }} data-testid={`rates-currency-${c}`}
                            className={`px-3 py-1 text-xs font-bold rounded-full transition ${cur === c ? "bg-emerald-500 text-white" : "text-slate-600"}`}>{c}</button>
                    ))}
                </div>
            </div>
            {rows.length === 0 && <div className="p-8 text-center text-sm text-slate-500" style={{ minHeight: 280 }}>Loading rates...</div>}
            <div className="divide-y divide-slate-100">
                {rows.map((c, i) => {
                    const pct = Math.round(c.r * 100);
                    return (
                        <div key={c.code} className="px-5 sm:px-6 py-3.5 group hover:bg-slate-50/60 transition-colors" data-testid={`rate-row-${c.code}`}>
                            <div className="flex items-center gap-3">
                                <GiftCardIcon code={c.code} iconUrl={c.icon_url} size={32} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className="font-semibold text-slate-800 truncate">{c.name}</span>
                                        <span className="text-sm whitespace-nowrap"><span className="text-slate-400 hidden sm:inline">You get </span><b className="text-emerald-600">{sym}{(100 * c.r).toFixed(2)}</b></span>
                                    </div>
                                    <div className="mt-1.5 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                                        <div className={`h-full rounded-full bg-gradient-to-r ${BAR[i % BAR.length]} transition-all ease-out`}
                                            style={{ width: on ? `${Math.max(6, (c.r / top) * 100)}%` : "0%", transitionDuration: "1.1s", transitionDelay: `${i * 90}ms` }} />
                                    </div>
                                    <div className="mt-1 flex justify-between text-[11px] text-slate-400">
                                        <span>{pct}% rate</span>
                                        <span>{sym}{c.min_amount} – {sym}{Number(c.max_amount || 0).toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {rows.length > 0 && (
                <Link to="/register" className="flex items-center justify-center gap-2 py-3 text-sm font-semibold text-emerald-700 bg-emerald-50/60 hover:bg-emerald-50 transition-colors">
                    <Zap size={14} /> Sell a card at these rates <ArrowRight size={14} />
                </Link>
            )}
        </div>
    );
}

// ── 3D mascot (woman holding a gift card + cash) ────────────────────────────
// Put the picture at frontend/public/images/woman-giftcard.png (transparent PNG).
// Hidden automatically if the file isn't there.
export function Mascot({ src = "/images/woman-giftcard.png", className = "", fallback = null }) {
    const [ok, setOk] = useState(true);
    const [ref, on] = useInView({ threshold: 0.2 });
    if (!ok) return fallback;
    return (
        <div ref={ref} className={`relative ${className}`}>
            <div aria-hidden className="absolute inset-x-6 bottom-2 h-6 rounded-full bg-black/30 blur-xl" />
            <img src={src} alt="Happy customer holding a gift card and cash" loading="lazy" onError={() => setOk(false)}
                className="relative w-full h-full object-contain drop-shadow-2xl"
                style={{
                    opacity: on ? 1 : 0, transform: on ? "none" : "translateY(40px) scale(.92)",
                    transition: "opacity .8s ease, transform .9s cubic-bezier(.2,.8,.2,1.2)",
                    animation: on ? "g2cFloat 5s ease-in-out 1s infinite" : "none",
                }} />
            <style>{"@keyframes g2cFloat{0%,100%{translate:0 0}50%{translate:0 -12px}}@media (prefers-reduced-motion: reduce){img{animation:none!important}}"}</style>
        </div>
    );
}
